import React, { useState, useEffect } from 'react';
import { ArrowLeft, RefreshCw, Mail, Phone, Building2, Calendar, MessageSquare, User, Loader2, Inbox } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contacts`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <ArrowLeft size={20} />
                <span>Volver al sitio</span>
              </a>
              <div className="h-6 w-px bg-slate-700" />
              <img
                src={companyInfo.logo}
                alt={companyInfo.name}
                className="h-10 w-auto object-contain"
              />
            </div>
            <Button
              onClick={fetchMessages}
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {loading ? (
                <Loader2 className="mr-2 animate-spin" size={18} />
              ) : (
                <RefreshCw className="mr-2" size={18} />
              )}
              Actualizar
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Panel de Administración</h1>
          <p className="text-slate-400">Mensajes recibidos del formulario de contacto</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Inbox className="text-blue-400" size={24} />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{messages.length}</p>
                <p className="text-slate-400 text-sm">Total de mensajes</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Building2 className="text-green-400" size={24} />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">
                  {messages.filter(m => m.company).length}
                </p>
                <p className="text-slate-400 text-sm">Con empresa</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <Calendar className="text-orange-400" size={24} />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">
                  {messages.filter(m => {
                    const msgDate = new Date(m.created_at);
                    const today = new Date();
                    return msgDate.toDateString() === today.toDateString();
                  }).length}
                </p>
                <p className="text-slate-400 text-sm">Hoy</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Messages list */}
          <div className="lg:col-span-2">
            <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h2 className="text-lg font-semibold text-white">Mensajes Recibidos</h2>
              </div>
              
              {loading ? (
                <div className="p-12 text-center">
                  <Loader2 className="animate-spin text-orange-400 mx-auto mb-4" size={40} />
                  <p className="text-slate-400">Cargando mensajes...</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="p-12 text-center">
                  <Inbox className="text-slate-600 mx-auto mb-4" size={48} />
                  <p className="text-slate-400">No hay mensajes aún</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-700 max-h-[600px] overflow-y-auto">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      onClick={() => setSelectedMessage(message)}
                      className={`p-4 cursor-pointer transition-colors hover:bg-slate-700/50 ${
                        selectedMessage?.id === message.id ? 'bg-slate-700/50 border-l-4 border-orange-500' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <User size={16} className="text-slate-500" />
                            <p className="font-medium text-white truncate">{message.name}</p>
                          </div>
                          <p className="text-sm text-slate-400 truncate">{message.email}</p>
                          <p className="text-sm text-slate-500 mt-2 line-clamp-2">{message.message}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-slate-500">
                            {new Date(message.created_at).toLocaleDateString('es-CO')}
                          </p>
                          <p className="text-xs text-slate-600">
                            {new Date(message.created_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Message detail */}
          <div className="lg:col-span-1">
            <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden sticky top-6">
              <div className="p-4 border-b border-slate-700">
                <h2 className="text-lg font-semibold text-white">Detalle del Mensaje</h2>
              </div>
              
              {selectedMessage ? (
                <div className="p-6 space-y-6">
                  {/* Contact info */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                        <User className="text-blue-400" size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-white">{selectedMessage.name}</p>
                        <p className="text-sm text-slate-400">Contacto</p>
                      </div>
                    </div>
                    
                    <a 
                      href={`mailto:${selectedMessage.email}`}
                      className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
                    >
                      <Mail className="text-orange-400" size={18} />
                      <span className="text-slate-300 text-sm">{selectedMessage.email}</span>
                    </a>
                    
                    {selectedMessage.phone && (
                      <a 
                        href={`tel:${selectedMessage.phone}`}
                        className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
                      >
                        <Phone className="text-green-400" size={18} />
                        <span className="text-slate-300 text-sm">{selectedMessage.phone}</span>
                      </a>
                    )}
                    
                    {selectedMessage.company && (
                      <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                        <Building2 className="text-purple-400" size={18} />
                        <span className="text-slate-300 text-sm">{selectedMessage.company}</span>
                      </div>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <MessageSquare className="text-slate-500" size={16} />
                      <p className="text-sm font-medium text-slate-400">Mensaje</p>
                    </div>
                    <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
                        {selectedMessage.message}
                      </p>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="pt-4 border-t border-slate-700">
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <Calendar size={14} />
                      <span>{formatDate(selectedMessage.created_at)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <a
                      href={`mailto:${selectedMessage.email}?subject=Re: Consulta desde MAGNETO Servicios Informáticos`}
                      className="flex-1"
                    >
                      <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                        <Mail className="mr-2" size={16} />
                        Responder
                      </Button>
                    </a>
                    {selectedMessage.phone && (
                      <a href={`tel:${selectedMessage.phone}`}>
                        <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                          <Phone size={16} />
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <MessageSquare className="text-slate-600 mx-auto mb-4" size={48} />
                  <p className="text-slate-400">Selecciona un mensaje para ver los detalles</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
